import React, { useState, useMemo, memo, useEffect, useRef, forwardRef } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { COLOR, THEME, __WEB__, __IOS__ } from '../../../constants/styles';

import {
  I18n,
  Utils,
  DatePicker,
  Select,
  DateUtils,
  FormInput,
  FormHelpers,
  FieldLabel,
  PhoneInput,
} from '../../components';

import { useSelector } from 'react-redux';
import { getRanksSelectOptions } from '../../../selectors/entities';

import moment from 'moment';

import {
  PHYSICIAN,
  PHYSICIAN_ASSISTANT,
  NURSE_PRACTITIONER,
  REGISTERED_NURSE,
  UNIT_DEPLOYMENT_MGR,
  COMMANDER,
  ADMINISTRATOR,
} from '../../../constants/staff_roles';
import {
  ACTIVE_DUTY,
  BENEFICIARY,
  RESERVE_DUTY,
  RETIREE,
  CIVILIAN_STAFF,
} from '../../../constants/patientRoles';
import {
  ROLE_TYPE_UDM,
  ROLE_TYPE_MED_STAFF,
  ROLE_TYPE_PATIENT,
  ROLE_TYPE_COMMANDER,
} from '../../../constants/roleTypes';
import SERVICE_BRANCHES from '../../../constants/serviceBranches';
import TextUtils from '../../../textUtils';
const STATUSS = [ACTIVE_DUTY, RESERVE_DUTY, BENEFICIARY, RETIREE, CIVILIAN_STAFF];
const SERVICE_STATUSS = [ACTIVE_DUTY, RESERVE_DUTY, RETIREE];
const ACTIVE_SERVICE_STATUSS = [ACTIVE_DUTY, RESERVE_DUTY];
const ACTIVE_SERVICE_STATUS_OPTIONS = ACTIVE_SERVICE_STATUSS.reduce(Utils.optionReducer, []);
const STATUS_OPTIONS = STATUSS.reduce(Utils.optionReducer, []);
const STAFF_STATUSS = [ACTIVE_DUTY, RESERVE_DUTY, CIVILIAN_STAFF];
const STAFF_STATUS_OPTIONS = STAFF_STATUSS.reduce(Utils.optionReducer, []);
const SPECIAL_STATUSS = ['None', 'Training Status', 'Basic Trainee', 'Flight Status'];
const AUDIOGRAM_TYPE_OPTIONS = [
  'Annual',
  'Reference/Initial Test',
  'Retirement/Separation',
  'Follow-Up Audiogram',
  'Pre-deployment/Post-deployment',
].reduce(Utils.optionReducer, []);
const SPECIAL_STATUS_OPTIONS = SPECIAL_STATUSS.reduce(Utils.optionReducer, []);
const IMR_OPTIONS = [
  { id: true, label: 'Yes' },
  { id: false, label: 'No' },
];
const SERVICE_BRANCH_OPTIONS = Object.values(SERVICE_BRANCHES).map((b) =>
  Utils.optionMapper(b, TextUtils.ce(b))
);
const APPOINTMENT_REASON_OPTIONS = [
  'Acute',
  'Chronic',
  'Current Diagnosis',
  'Med Refill',
  'New Concern',
  'Profile',
].map((v) => Utils.optionMapper(v));

const allFields = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'upcomingApptDate',
  'status',
  'serviceBranch',
  'rank',
  'group',
  'dodId',
  'department',
  'imr',
  'specialStatus',
  'role',
  'medicalService',
  'clinicalLocation',
  'appointmentReason',
  'audiogramType',
];

const optionalFields = ['upcomingApptDate', 'imr', 'appointmentReason', 'audiogramType'];

function dateFromDateStr(str) {
  let date = DateUtils.sanitizeDate(str);
  const retVal = date || (__WEB__ ? null : new Date());
  return retVal;
}

export default memo(
  forwardRef((props, ref) => {
    const isVerticalLayout = props.isVerticalLayout !== false;
    const isMobileWeb = props.isMobileWeb === true;
    const initialRole =
      props.roleType === ROLE_TYPE_UDM
        ? UNIT_DEPLOYMENT_MGR
        : props.roleType === ROLE_TYPE_COMMANDER
          ? COMMANDER
          : null;

    const ranksOptions = useSelector(getRanksSelectOptions);
    const [firstName, setFirstName] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [email, setEmail] = useState(null);
    const [phone, setPhone] = useState(null);
    const [upcomingApptDate, _setUpcomingApptDate] = useState(null);
    const setUpcomingApptDate = (date) => {
      if (date) _setUpcomingApptDate(new Date(date));
    };
    const [status, _setStatus] = useState(null);
    const setStatus = (newStatus) => {
      if (status !== newStatus) {
        _setStatus(newStatus);
        if (ACTIVE_SERVICE_STATUSS.includes(newStatus)) {
          if (!serviceBranch) setServiceBranch(SERVICE_BRANCHES.AIR_FORCE);
        } else {
          setServiceBranch(null);
          setRank(null);
          setGroup(null);
        }
      }
    };
    const [serviceBranch, _setServiceBranch] = useState(null);
    const setServiceBranch = (branch) => {
      _setServiceBranch(branch);
      setRank(null);
      setGroup(null);
    };
    const [rank, setRank] = useState(null);
    const [group, setGroup] = useState(null);
    const [dodId, setDodId] = useState(null);
    const [department, setDepartment] = useState(null);
    const [imr, setImr] = useState(null);
    const [appointmentReason, setAppointmentReason] = useState(null);
    const [audiogramType, setAudiogramType] = useState(null);
    const [role, setRole] = useState(initialRole);
    const [specialStatus, setSpecialStatus] = useState(null);
    const [medicalService, setMedicalService] = useState(null);
    const [clinicalLocation, setClinicalLocation] = useState(null);
    const [curSelect, setCurSelect] = useState('firstName');

    const omittedFields = useMemo(() => {
      const fields = [...(props.omittedFields || [])];
      optionalFields.forEach((field) => {
        if (!props.optionalFields?.includes(field)) {
          fields.push(field);
        }
      });
      return fields;
    }, [props.omittedFields, props.optionalFields]);

    function clearCurSelect() {
      setCurSelect(null);
    }

    useEffect(() => {
      const initialStatus = props.formData?.status || ACTIVE_DUTY;
      const initialServiceBranch =
        props.formData?.serviceBranch ||
        (ACTIVE_SERVICE_STATUSS.includes(initialStatus) ? SERVICE_BRANCHES.AIR_FORCE : null);

      setFirstName(props.formData?.firstName || null);
      setLastName(props.formData?.lastName || null);
      setEmail(props.formData?.email || null);
      setPhone(props.formData?.phone || null);
      setUpcomingApptDate(dateFromDateStr(props.formData?.upcomingApptDate));
      setStatus(initialStatus);
      setServiceBranch(initialServiceBranch);
      setRank(props.formData?.rank || null);
      setGroup(props.formData?.group || null);
      setDodId(props.formData?.dodId || null);
      setDepartment(props.formData?.department || null);
      setImr(props.formData?.imr || null);
      setAppointmentReason(props.formData?.appointmentReason || null);
      setAudiogramType(props.formData?.audiogramType || null);
      setRole(props.formData?.role || initialRole || null);
      setSpecialStatus(props.formData?.specialStatus || null);
      setMedicalService(props.formData?.medicalService || null);
      setClinicalLocation(props.formData?.clinicalLocation || null);
    }, [props.formData]);

    const rolesOptions = useMemo(() => {
      const rolesToFilter =
        props.roleType === ROLE_TYPE_MED_STAFF ? [UNIT_DEPLOYMENT_MGR, COMMANDER] : [];
      const roles = rolesToFilter.includes(initialRole)
        ? props.roles
        : (props.roles || []).filter((r) => !rolesToFilter.includes(r));
      return Utils.alphabeticalOptionMaker(roles);
    }, [props.roles, props.roleType]);
    const groupsOptions = useMemo(
      () => Utils.alphabeticalOptionMaker(props.groups),
      [props.groups]
    );
    const clinicalLocationsOptions = useMemo(
      () => Utils.alphabeticalOptionMaker(props.clinicalLocations),
      [props.clinicalLocations]
    );
    const medicalServicesOptions = useMemo(
      () => Utils.alphabeticalOptionMaker(props.medicalServices),
      [props.medicalServices]
    );
    const departmentOptions = useMemo(
      () => (props.departments || []).map((d) => ({ label: d.name, id: d.employer_department_id })),
      [props.departments]
    );

    // stringify in case non properly memoized object is passed in, but if it is properly memoized this will not cause unnecessary renders
    const requiredFieldsStringified = useMemo(
      () => JSON.stringify(props.requiredFields),
      [props.requiredFields]
    );

    const refs = useRef(null);
    if (!refs.current) {
      refs.current = {};
      allFields.forEach((field) => {
        refs.current[field] = React.createRef();
      });
    }
    function validate() {
      const fieldsToValidate = allFields.map((field) => refs.current[field].current);
      return FormHelpers.validateFields(fieldsToValidate);
    }
    function fixupData(data) {
      const requestObj = {
        email: data.email?.trim().toLowerCase(),
        firstName: Utils.validate.sanitizeName(data.firstName),
        lastName: Utils.validate.sanitizeName(data.lastName),
        name: Utils.validate.sanitizeName(
          `${data.firstName?.trim() || ''} ${data.lastName?.trim() || ''}`.trim()
        ),
        rank: data.rank,
        group: data.group,
        role: data.role || null,
        clinicalLocation: data.clinicalLocation,
        medicalService: data.medicalService,
        phone: data.phone,
        upcomingApptDate: data.upcomingApptDate
          ? moment(data.upcomingApptDate).format('YYYY-MM-DD')
          : null,
        dod_id: data.dodId?.trim(),
        patientRole: data.status,
        employer_department_id: data.department,
        special_status: data.specialStatus,
        service_branch: data.serviceBranch,
        imr: data.imr,
        appointmentReason: data.appointmentReason,
        audiogramType: data.audiogramType,
      };
      if (!requestObj.clinicalLocation || !refs.current?.clinicalLocation?.current)
        requestObj.clinicalLocation = null;
      if (!requestObj.medicalService || !refs.current?.medicalService?.current)
        requestObj.medicalService = null;
      if (!requestObj.group || !refs.current?.group?.current) requestObj.group = null;
      if (!requestObj.rank || !refs.current?.rank?.current) requestObj.rank = null;
      if (!requestObj.dod_id || !refs.current?.dodId?.current) delete requestObj.dod_id;
      if (!requestObj.imr || !refs.current?.imr?.current) delete requestObj.imr;
      if (!requestObj.appointmentReason || !refs.current?.appointmentReason?.current)
        delete requestObj.appointmentReason;
      if (!requestObj.audiogramType || !refs.current?.audiogramType?.current)
        delete requestObj.audiogramType;
      if (!requestObj.employer_department_id || !refs.current?.department?.current)
        delete requestObj.employer_department_id;
      if (!requestObj.special_status || !refs.current?.specialStatus?.current)
        delete requestObj.special_status;
      if (!requestObj.service_branch || !refs.current?.serviceBranch?.current)
        delete requestObj.service_branch;
      if (!refs.current?.upcomingApptDate?.current) delete requestObj.upcomingApptDate;
      return requestObj;
    }

    function getObjectToSubmit() {
      const obj = {
        firstName,
        lastName,
        email,
        phone,
        upcomingApptDate,
        status,
        serviceBranch,
        rank,
        group,
        dodId,
        department,
        imr,
        specialStatus,
        role,
        medicalService,
        clinicalLocation,
        appointmentReason,
        audiogramType,
      };
      const valid = validate();
      if (!valid) return null;
      return fixupData(obj);
    }

    React.useImperativeHandle(ref, () => ({
      getObjectToSubmit,
    }));
    useEffect(() => {
      const data = {
        firstName,
        lastName,
        email,
        phone,
        upcomingApptDate,
        status,
        serviceBranch,
        rank,
        group,
        dodId,
        department,
        imr,
        role,
        specialStatus,
        medicalService,
        clinicalLocation,
        appointmentReason,
        audiogramType,
      };
      props.onDataChange?.(fixupData(data));
    }, [
      firstName,
      lastName,
      email,
      phone,
      upcomingApptDate,
      status,
      serviceBranch,
      rank,
      group,
      dodId,
      department,
      imr,
      role,
      specialStatus,
      medicalService,
      clinicalLocation,
      appointmentReason,
      audiogramType,
      requiredFieldsStringified,
    ]);

    const isServiceStatus = SERVICE_STATUSS.includes(status);
    const isActiveServiceStatus = ACTIVE_SERVICE_STATUSS.includes(status);

    const showServiceBranch = isServiceStatus || status === CIVILIAN_STAFF;
    const showRank = isServiceStatus;
    const showGroup = serviceBranch === SERVICE_BRANCHES.AIR_FORCE && isActiveServiceStatus;
    const showRole = props.roleType !== ROLE_TYPE_PATIENT;

    const showClinicalLocationAndMedService = [
      PHYSICIAN,
      PHYSICIAN_ASSISTANT,
      NURSE_PRACTITIONER,
      REGISTERED_NURSE,
      ADMINISTRATOR,
    ].includes(role);
    const requireClinicalLocationAndMedService = role !== ADMINISTRATOR;

    const focusHandlers = useMemo(function () {
      return allFields.reduce((result, field) => {
        result[field] = () => {
          setTimeout(() => {
            setCurSelect(field);
          });
        };
        return result;
      }, {});
    }, []);

    function renderField(name, field, extraStyle) {
      return (
        <View
          key={name}
          style={[
            curSelect === name ? { zIndex: 1000 } : { zIndex: __WEB__ ? -1 : 1 },
            extraStyle,
            isVerticalLayout ? styles.verticalFieldContainer : styles.horizontalFieldContainer,
          ]}
        >
          {field}
        </View>
      );
    }
    const fieldRenderers = {};
    fieldRenderers.firstName = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.firstName)
      );
      return renderField(
        'firstName',
        <FormInput
          ref={refs.current.firstName}
          value={firstName}
          label={I18n.ac('auth.firstName')}
          required={true}
          onChangeText={setFirstName}
          editable={!readOnly}
          disabled={props.disabled}
          controlled={true}
          autoCorrect={false}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.lastName = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.lastName)
      );
      return renderField(
        'lastName',
        <FormInput
          ref={refs.current.lastName}
          value={lastName}
          label={I18n.ac('auth.lastName')}
          required={true}
          onChangeText={setLastName}
          editable={!readOnly}
          disabled={props.disabled}
          controlled={true}
          autoCorrect={false}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.email = () => {
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.email));
      return renderField(
        'email',
        <FormInput
          ref={refs.current.email}
          value={email}
          autoCapitalize="none"
          label={I18n.ac('form.emailAddress')}
          required={true}
          validateFunc={Utils.validate.email}
          invalidText={I18n.t('form.emailInvalid')}
          onChangeText={setEmail}
          editable={!readOnly}
          disabled={props.disabled}
          controlled={true}
          autoCorrect={false}
          flexibleHeight={isVerticalLayout}
        />
      );
    };
    fieldRenderers.phone = () => {
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.phone));
      return renderField(
        'phone',
        <PhoneInput
          ref={refs.current.phone}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.phone}
          value={phone}
          label={I18n.ac('auth.mobilePhone')}
          required={true}
          disabled={props.disabled}
          editable={!readOnly}
          invalidText={`${I18n.t('form.phoneInvalid')} ${props.tips?.phone || ''}`}
          onChangeText={setPhone}
          flexibleHeight={!!isVerticalLayout}
          webMode={!isMobileWeb}
        />
      );
    };
    fieldRenderers.upcomingApptDate = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.upcomingApptDate)
      );
      const renderedField = renderField(
        'upcomingApptDate',
        <DatePicker
          ref={refs.current.upcomingApptDate}
          label="Upcoming Appointment Date"
          placeholder="Upcoming Appointment"
          extraLabel="(if applicable)"
          validateFunc={Utils.validate.date}
          invalidText={'Please enter a valid date'}
          required={false}
          mode="date"
          webModal={true}
          onFocus={focusHandlers.upcomingApptDate}
          onBlur={clearCurSelect}
          onDate={setUpcomingApptDate}
          selectedDate={upcomingApptDate}
          disabled={props.disabled}
          readOnly={!!readOnly}
          calendarIcon={
            <Image
              style={styles.calImage}
              source={require('../../../../resources/nav-icon-calendar.png')}
            />
          }
          flexibleHeight={!!__WEB__ && !!isVerticalLayout}
        />,
        !!__IOS__ ? { height: 250, marginTop: 0 } : { marginTop: 0 }
      );
      if (!__WEB__) {
        return (
          <View key="upcomingApptDate" style={{ marginTop: 35 }}>
            <FieldLabel
              value={true}
              disabled={!!readOnly}
              required={false}
              label={'Upcoming Appointment Date (if applicable)'}
              style={{ marginBottom: 0 }}
            />
            {renderedField}
          </View>
        );
      } else {
        return renderedField;
      }
    };
    fieldRenderers.status = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.status)
      );
      return renderField(
        'status',
        <Select
          ref={refs.current.status}
          required={true}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.status}
          options={
            props.patientRoles
              ? props.patientRoles.reduce(Utils.optionReducer, [])
              : props.activeDutyOnly
                ? ACTIVE_SERVICE_STATUS_OPTIONS
                : props.roleType === ROLE_TYPE_MED_STAFF
                  ? STAFF_STATUS_OPTIONS
                  : STATUS_OPTIONS
          }
          label={I18n.ac('airforce.status')}
          value={status}
          onSelect={setStatus}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'auto'}
          flexibleHeight={isVerticalLayout}
        />
      );
    };
    fieldRenderers.serviceBranch = () => {
      const isRequired = isActiveServiceStatus;
      if (!showServiceBranch) return null;
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.serviceBranch)
      );
      return renderField(
        'serviceBranch',
        <Select
          ref={refs.current.serviceBranch}
          required={isRequired}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.serviceBranch}
          options={SERVICE_BRANCH_OPTIONS}
          label="Service Branch"
          value={serviceBranch}
          onSelect={setServiceBranch}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'top'}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.rank = () => {
      const isRequired = isActiveServiceStatus;
      if (!showRank) return null;
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.rank));
      return renderField(
        'rank',
        <Select
          ref={refs.current.rank}
          required={isRequired}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.rank}
          options={ranksOptions[serviceBranch] || []}
          label={I18n.ac('airforce.rank')}
          value={rank}
          onSelect={setRank}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'top'}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.group = () => {
      if (!showGroup) return null;
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.group));
      return renderField(
        'group',
        <Select
          ref={refs.current.group}
          required={true}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.group}
          options={groupsOptions}
          label={I18n.ac('airforce.squadron')}
          value={group}
          onSelect={setGroup}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'top'}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.dodId = () => {
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.dodId));
      const required = status !== CIVILIAN_STAFF;
      return renderField(
        'dodId',
        <FormInput
          ref={refs.current.dodId}
          value={dodId}
          label="DOW ID"
          required={required}
          onChangeText={setDodId}
          editable={!readOnly}
          disabled={props.disabled}
          controlled={true}
          maxLength={10}
          validateFunc={Utils.validate.dodId}
          invalidText="DOW ID must be exactly 10 characters"
          requiredErrorText="DOW ID Required"
          keyboardType="numeric"
          flexibleHeight={!!isVerticalLayout}
          hasCopyButton={true}
        />
      );
    };
    fieldRenderers.specialStatus = () => {
      if (!props.showSpecialStatus) return null;
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.specialStatus)
      );
      return renderField(
        'specialStatus',
        <Select
          ref={refs.current.specialStatus}
          required={false}
          closeOnSelect={true}
          options={SPECIAL_STATUS_OPTIONS}
          label={I18n.t('airforce.specialStatus')}
          value={specialStatus}
          onValue={setSpecialStatus}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'auto'}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.department = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.department)
      );
      // const required = !!props.requiredFields?.department || (isMacDillAudiogramOrSpecialtyHearing && status === CIVILIAN_STAFF);
      const required = !!props.requiredFields?.department;
      return renderField(
        'department',
        <Select
          ref={refs.current.department}
          required={required}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.department}
          options={departmentOptions || []}
          label={'Department / Shop'}
          value={department}
          onSelect={setDepartment}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'auto'}
          flexibleHeight={!!isVerticalLayout}
          isClearable={!required && !props.formData?.department}
        />
      );
    };
    fieldRenderers.audiogramType = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.audiogramType)
      );
      return renderField(
        'audiogramType',
        <Select
          ref={refs.current.audiogramType}
          required={true}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.audiogramType}
          options={AUDIOGRAM_TYPE_OPTIONS}
          label={'Audiogram Type'}
          value={audiogramType}
          onSelect={setAudiogramType}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={isVerticalLayout ? 'bottom' : 'auto'}
          flexibleHeight={!!isVerticalLayout}
          isClearable={false}
          tipText={props.tips?.audiogramType}
        />
      );
    };
    fieldRenderers.imr = () => {
      const readOnly = !!(props.readOnly || (props.readOnlyUnlessMissing && props.formData?.imr));
      const required = !!props.requiredFields?.imr;
      return renderField(
        'imr',
        <Select
          ref={refs.current.imr}
          required={required}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.imr}
          options={IMR_OPTIONS}
          label={'IMR'}
          value={imr}
          onSelect={setImr}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={'auto'}
          flexibleHeight={!!isVerticalLayout}
          isClearable={!required && !props.formData?.imr}
        />
      );
    };
    fieldRenderers.appointmentReason = () => {
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.appointmentReason)
      );
      const required = !!props.requiredFields?.appointmentReason;
      return renderField(
        'appointmentReason',
        <Select
          ref={refs.current.appointmentReason}
          required={required}
          closeOnSelect={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.appointmentReason}
          options={APPOINTMENT_REASON_OPTIONS}
          label={'Appointment reason'}
          value={appointmentReason}
          onSelect={setAppointmentReason}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          menuPlacement={'auto'}
          flexibleHeight={!!isVerticalLayout}
          isClearable={!required && !props.formData?.appointmentReason}
        />
      );
    };
    fieldRenderers.role = () => {
      if (!showRole) return null;
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.role) ||
        initialRole === COMMANDER ||
        initialRole === UNIT_DEPLOYMENT_MGR
      );
      return renderField(
        'role',
        <Select
          ref={refs.current.role}
          required={true}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.role}
          closeOnSelect={true}
          options={rolesOptions}
          label={I18n.ac('staff.role')}
          value={role}
          onSelect={setRole}
          webMode={!isMobileWeb}
          editable={!readOnly}
          disabled={props.disabled}
          flexibleHeight={!!isVerticalLayout}
        />
      );
    };
    fieldRenderers.medicalService = () => {
      if (!showClinicalLocationAndMedService) return null;
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.medicalService)
      );
      return renderField(
        'medicalService',
        <Select
          ref={refs.current.medicalService}
          required={requireClinicalLocationAndMedService}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.medicalService}
          closeOnSelect={true}
          options={medicalServicesOptions}
          label={I18n.ac('staff.medicalService')}
          value={medicalService}
          onSelect={setMedicalService}
          webMode={!isMobileWeb}
          menuPlacement="top"
          editable={!readOnly}
          disabled={props.disabled}
          flexibleHeight={!!isVerticalLayout}
          isClearable={!requireClinicalLocationAndMedService && !props.formData?.medicalService}
        />
      );
    };
    fieldRenderers.clinicalLocation = () => {
      if (!showClinicalLocationAndMedService) return null;
      const readOnly = !!(
        props.readOnly ||
        (props.readOnlyUnlessMissing && props.formData?.clinicalLocation)
      );
      return renderField(
        'clinicalLocation',
        <Select
          ref={refs.current.clinicalLocation}
          required={requireClinicalLocationAndMedService}
          onBlur={clearCurSelect}
          onFocus={focusHandlers.clinicalLocation}
          closeOnSelect={true}
          options={clinicalLocationsOptions}
          label={I18n.ac('staff.clinicalLocation')}
          value={clinicalLocation}
          onSelect={setClinicalLocation}
          webMode={!isMobileWeb}
          menuPlacement="top"
          editable={!readOnly}
          disabled={props.disabled}
          flexibleHeight={!!isVerticalLayout}
          isClearable={!requireClinicalLocationAndMedService && !props.formData?.clinicalLocation}
        />
      );
    };

    const renderFieldWithName = (fieldName) => {
      if (omittedFields?.includes(fieldName)) return null;
      return fieldRenderers[fieldName]?.();
    };

    function renderVertical() {
      return (
        <View style={[styles.fields, styles.fieldsVertical]}>
          <View style={[THEME.inputsContainer, { backgroundColor: COLOR.contrast }]}>
            {allFields.map(renderFieldWithName)}
          </View>
        </View>
      );
    }

    function renderHorizontalRow(fields) {
      // console.info('>> renderHorizontalRow', fields);
      if (!fields.length) return null;
      return (
        <View
          key={fields.join('-')}
          style={[styles.fieldsRow, fields.includes(curSelect) ? { zIndex: 1000 } : { zIndex: -1 }]}
        >
          {fields.map(renderFieldWithName)}
          {fields.length < 3 && <View style={styles.horizontalFieldContainer} />}
          {fields.length < 2 && <View style={styles.horizontalFieldContainer} />}
        </View>
      );
    }

    function renderHorizontal() {
      // const firstRowFields = [
      //   'firstName',
      //   'lastName',
      //   'upcomingApptDate',
      //   'audiogramType',
      // ];
      // const secondRowFields = ['email', 'phone', 'status'];

      const firstRowFields = ['firstName', 'lastName', 'dodId'];
      const secondRowFields = [];

      const moreFields = [
        'status',
        showServiceBranch ? 'serviceBranch' : null,
        showRank ? 'rank' : null,
        'email',
        'phone',
        'upcomingApptDate',
        'audiogramType',
        showGroup ? 'group' : null,
        'department',
        'appointmentReason',
        'imr',
        showRole ? 'role' : null,
      ]
        .filter(Boolean)
        .filter((field) => !omittedFields?.includes(field));

      const moreRows = moreFields.reduce((rows, field, index) => {
        const rowIndex = Math.floor(index / 3);
        if (!rows[rowIndex]) rows[rowIndex] = [];
        rows[rowIndex].push(field);
        return rows;
      }, []);

      // const thirdRowFields = [
      //   'email',
      //   'upcomingApptDate',
      //   'audiogramType',
      // ].filter(fieldName => !omittedFields?.includes(fieldName));
      // const fourthRowFields = [
      //   'department', 'appointmentReason', 'imr'
      // ].filter(fieldName => !omittedFields?.includes(fieldName));

      // if (thirdRowFields.length + fourthRowFields.length <= 3) {
      //   thirdRowFields.push(...fourthRowFields);
      //   fourthRowFields.length = 0;
      // };

      // const thirdRowFields = showServiceBranch
      //   ? ['serviceBranch', 'dodId', 'department', 'appointmentReason', 'imr']
      //   : ['dodId', 'department', 'appointmentReason', 'imr'];
      // const fourthRowFields = [];
      // const fifthRowFields = [];
      // if (showRank) fourthRowFields.push('rank');
      // if (showGroup) fourthRowFields.push('group');
      // if (showRole) fourthRowFields.push('role');
      if (showClinicalLocationAndMedService) {
        const lastRow = moreRows[moreRows.length - 1];
        if (lastRow && lastRow.length === 1) lastRow.push('medicalService', 'clinicalLocation');
        else moreRows.push(['medicalService', 'clinicalLocation']);
      }
      // console.info('>> moreRows', moreRows);
      const rows = [firstRowFields, secondRowFields, ...moreRows];
      const filteredRows = rows
        .map((row) => row.filter(Boolean).filter((field) => !omittedFields?.includes(field)))
        .filter((row) => row.length > 0);
      return (
        <View style={[styles.fields, styles.fieldsHorizontal]}>
          {filteredRows.map((fields) => renderHorizontalRow(fields))}
        </View>
      );
    }

    return (
      <View style={[{ zIndex: 1000, paddingBottom: 30 }]}>
        {isVerticalLayout ? renderVertical() : renderHorizontal()}
      </View>
    );
  })
);

const styles = StyleSheet.create({
  horizontalFieldContainer: {
    flex: 1,
    paddingRight: 40,
  },
  verticalFieldContainer: {
    marginVertical: 10,
  },
  fields: {
    zIndex: 10,
  },
  fieldsVertical: {
    flexDirection: 'column',
  },
  fieldsHorizontal: {
    paddingBottom: 0,
    paddingTop: 10,
  },
  fieldsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 0,
    marginTop: 0,
    paddingTop: 0,
    paddingLeft: 35,
    paddingBottom: 20,
  },
});
